import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { KeyRound, Eye, EyeOff, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page that consumes a one-time invite token from the URL and lets
 * the lease tenant set their password. On success the user is logged in
 * and redirected to /my/lease.
 */
export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setSessionFromTokenResponse } = useAuth();

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("No invite token in this link. Ask the host to resend."); return; }
    (async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/auth/invite-info`, { token });
        setInfo(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not validate invite link.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/set-password`,
        { token, new_password: pwd },
        { withCredentials: true },
      );
      // Hand the freshly-issued token to the auth context so the SPA
      // recognises us without bouncing through /login.
      if (setSessionFromTokenResponse) setSessionFromTokenResponse(data);
      toast.success("Password set — welcome to the portal!");
      navigate("/my/lease");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B4332] via-[#2D6A4F] to-[#40916C] flex items-center justify-center p-6" data-testid="set-password-page">
      <Card className="w-full max-w-md border-[#E8E8E6] shadow-2xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto bg-emerald-50 rounded-full p-3 w-fit">
            <KeyRound className="h-7 w-7 text-emerald-700" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            Welcome to the tenant portal
          </CardTitle>
          {info && (
            <CardDescription className="text-sm">
              <strong className="text-[#1B4332]">{info.organization_name || "Mission Hospitality"}</strong>
              <br />
              Set a password for <strong>{info.email}</strong>
            </CardDescription>
          )}
          {error && (
            <CardDescription className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-2 text-left flex items-start gap-2" data-testid="set-password-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </CardDescription>
          )}
          {!info && !error && (
            <CardDescription className="flex items-center justify-center gap-2 text-[#7A8A84]">
              <Loader2 className="h-4 w-4 animate-spin" /> Validating invite…
            </CardDescription>
          )}
        </CardHeader>
        {info && !error && (
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="pwd1">New password</Label>
                <div className="relative">
                  <Input
                    id="pwd1"
                    type={show ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    minLength={6}
                    required
                    autoFocus
                    data-testid="set-password-input-1"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7A8A84] hover:text-[#1B4332]"
                    tabIndex={-1}
                    data-testid="set-password-toggle-show"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-[#7A8A84]">Minimum 6 characters.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pwd2">Confirm password</Label>
                <Input
                  id="pwd2"
                  type={show ? "text" : "password"}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  minLength={6}
                  required
                  data-testid="set-password-input-2"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1B4332] hover:bg-[#2D6A4F]"
                data-testid="set-password-submit-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                {submitting ? "Setting password…" : "Set password & enter portal"}
              </Button>
            </form>
            <p className="text-[11px] text-[#7A8A84] mt-4 text-center">
              By continuing you confirm you are the lease holder named above.
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
